import clsx from 'clsx';
import { useEditorRef, useEditorSelector } from 'platejs/react';
import { useDropdown } from '../../hooks/useDropdown';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import styles from './FontFamilyMenu.module.scss';
import { applyMarkFast } from '../applyMarkFast';

// Each option pairs a stable id (used to match the active mark) with a CSS
// font-family stack. The stack is what we write to the Plate mark — passing
// the full stack (not just the display name) means the rendered text gracefully
// falls back when a face isn't installed on the reader's machine.
interface FontOption {
    id: string;
    label: string;
    stack: string;
}

// Gilroy is the brand default — its @font-face is loaded in src/scss/index.scss
// (Gilroy-Regular/Medium/SemiBold/Bold). We pin the dropdown's default to it so
// the editor's base font matches what the user sees in the trigger.
const GILROY_STACK = '"Gilroy-Regular", "Gilroy", -apple-system, BlinkMacSystemFont, sans-serif';

export const FONTS: FontOption[] = [
    { id: 'gilroy', label: 'Gilroy', stack: GILROY_STACK },
    { id: 'inter', label: 'Inter', stack: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' },
    { id: 'system', label: 'System', stack: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
    { id: 'arial', label: 'Arial', stack: 'Arial, Helvetica, sans-serif' },
    { id: 'helvetica', label: 'Helvetica', stack: 'Helvetica, Arial, sans-serif' },
    { id: 'georgia', label: 'Georgia', stack: 'Georgia, "Times New Roman", serif' },
    { id: 'times', label: 'Times New Roman', stack: '"Times New Roman", Times, serif' },
    { id: 'courier', label: 'Courier New', stack: '"Courier New", Courier, monospace' },
    { id: 'mono', label: 'Monospace', stack: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
    { id: 'verdana', label: 'Verdana', stack: 'Verdana, Geneva, sans-serif' },
    { id: 'tahoma', label: 'Tahoma', stack: 'Tahoma, Geneva, sans-serif' },
    { id: 'pacifico', label: 'Pacifico', stack: "'Pacifico', cursive" },
];

// When no fontFamily mark is on the active leaf, fall back to Gilroy in the
// trigger label — the editor's body CSS uses the same stack so the dropdown's
// label stays truthful for the rendered text.
const DEFAULT_OPTION = FONTS[0];

// Match an active fontFamily mark to one of our options. We compare the
// first family token (case-insensitive) so a saved value like
// `Inter, sans-serif` still matches the `Inter` option even if its stack
// has more fallbacks now than when it was saved.
export function matchOption(activeStack: string | undefined): FontOption {
    if (!activeStack) return DEFAULT_OPTION;
    const head = activeStack.split(',')[0]?.replace(/['"]/g, '').trim().toLowerCase();
    if (!head) return DEFAULT_OPTION;
    const hit = FONTS.find((o) => o.stack.split(',')[0]?.replace(/['"]/g, '').trim().toLowerCase() === head);
    return hit ?? DEFAULT_OPTION;
}

export function FontFamilyMenu() {
    const editor = useEditorRef();
    const { open, setOpen, ref: wrapRef } = useDropdown();

    const activeStack: string | undefined = useEditorSelector((ed: any) => ed?.api?.marks?.()?.fontFamily, []);
    const current = matchOption(activeStack);

    const apply = (option: FontOption) => (e: React.MouseEvent) => {
        e.preventDefault();
        const ed: any = editor;
        try {
            if (option.id === DEFAULT_OPTION.id) {
                // Picking the default (Gilroy) removes the explicit mark so
                // text inherits the editor's base font — keeps the persisted
                // document clean instead of writing Gilroy on every leaf.
                ed?.tf?.removeMarks?.('fontFamily');
            } else {
                // Single-pass apply — see applyMarkFast for the rationale.
                applyMarkFast(ed, 'fontFamily', option.stack, (v) => ed?.tf?.fontFamily?.addMark?.(v));
            }
        } catch {
            /* defensive */
        }
        setOpen(false);
    };

    return (
        <div className={styles.wrap} ref={wrapRef}>
            <button
                type="button"
                className={clsx(styles.trigger, open && styles.triggerOpen)}
                onMouseDown={(e) => {
                    e.preventDefault();
                    setOpen((o) => !o);
                }}
                aria-haspopup="true"
                aria-expanded={open}
                aria-label="Font family"
                title="Font family"
            >
                <span className={styles.triggerLabel}>{current.label}</span>
                <ArrowDropDownRoundedIcon className={styles.caret} fontSize="small" />
            </button>
            {open && (
                <div className={styles.popover} role="menu" aria-label="Pick font family">
                    {FONTS.map((opt) => (
                        <button
                            key={opt.id}
                            type="button"
                            role="menuitem"
                            className={clsx(styles.item, current.id === opt.id && styles.itemActive)}
                            onMouseDown={apply(opt)}
                            style={opt.stack ? { fontFamily: opt.stack } : undefined}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
